import storeBuilder from './utils/tokenStore';
import { verifyIdToken } from './utils/jwt';
import AuthError from './utils/errors/authError';
import TokenValidationError from './utils/errors/tokenValidationError';
import UnhandledAuthError from './utils/errors/unhandledAuthError';

/**
 *
 * @param {Object} params
 * @param {String} params.clientId - Auth0 client id
 * @param {String} params.audience - Auth0 Audience
 * @param {String} params.domain - Auth0 domain
 * @param {Boolean} [params.useRefreshTokens] - It should use refresh tokens?
 * @param {String} params.connection - Auth0 default connection
 * @param {('localStorage'|'memory')} params.cacheLocation - Where store the tokens
 * @param {boolean} [params.obfuscateCache=true] - Obfuscate tokens in cache/store
 * @returns {import('./types/auth').AuthObjectInterface} - Auth object
 */
const build = ({
    clientId,
    audience,
    domain,
    useRefreshTokens,
    connection,
    cacheLocation,
    obfuscateCache = true,
}) => {
    const tokenStore = storeBuilder({
        type: cacheLocation,
        clientId,
        audience,
        obfuscate: obfuscateCache,
    });

    /**
     * Makes the oauth request
     * @param {any} options
     * @returns {Promise<boolean>}
     */
    const oauthRequestAsync = async (options) => {
        try {
            const resp = await fetch(`https://${domain}/oauth/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(options),
            });
            const data = await resp.json();
            if (resp.ok) {
                let userData;
                try {
                    userData = verifyIdToken({
                        id_token: data.id_token,
                        aud: clientId,
                        iss: `https://${domain}/`,
                    });
                } catch (error) {
                    // TODO: devolver diferentes tipos de error
                    const err = new TokenValidationError(
                        'Token validation exception'
                    );
                    err.description = error.message;
                    throw err;
                }
                const tokens = {
                    accessToken: data.access_token,
                    idToken: { ...userData },
                    refreshToken: data.refresh_token,
                    scope: data.scope,
                    expiresAt: data.expires_in * 1000 + Date.now(),
                };
                tokenStore.set(tokens);
                return true;
            }
            const error = new AuthError(data.error);
            error.description = data.error_description;
            throw error;
        } catch (err) {
            if (!err.description) {
                const error = new UnhandledAuthError('Unhandled error');
                error.original = err;
                error.description =
                    'Unhandled error. Maybe a Network Error. Check console.';
                throw error;
            }
            throw err;
        }
    };

    /**
     * Relogin using refresh tokens
     * @param {string} refreshToken
     * @return {Promise<boolean>}
     */
    const refreshTokenReloginAsync = async (refreshToken) => {
        try {
            const options = {
                grant_type: 'refresh_token',
                client_id: clientId,
                response_type: 'token id_token',
                refresh_token: refreshToken,
            };
            await oauthRequestAsync(options);
            return true;
        } catch (error) {
            return false;
        }
    };

    /**
     * Returns tokens. It will try to relogin using refresh_token if it's needed
     * @returns {Promise<import('./types/auth').TokensObjectInterface>} - User tokens or undefined
     */
    const getTokensAsync = async () => {
        const tokens = tokenStore.get();
        if (tokens) {
            // Si tengo accessToken, la sesion es valida
            if (tokens.accessToken) {
                return tokens;
            }
            // Si tengo la posiblidad, intento relogear con refreshTokens
            if (useRefreshTokens && tokens.refreshToken) {
                const refreshResp = await refreshTokenReloginAsync(
                    tokens.refreshToken
                );
                // Si relogeo bien, devuelvo los nuevos tokens
                if (refreshResp) return tokenStore.get();
                // Si no, limpio el store para borrar el refreshToken invalido
                tokenStore.remove();
            } else {
                // Si por alguna razon useRefreshToken = false, pero tengo esa data en store (cambio de config por ej)
                tokenStore.remove();
            }
        }
        return undefined;
    };

    /**
     * Returns the user data from the id_oken, if the user is logged in (It will try to relogin with refreshToken if it is possible)
     * @returns {Promise<any>} - User data or undefined
     */
    const getUserDataAsync = async () => {
        const tokens = await getTokensAsync();
        if (tokens) return tokens.idToken.user;
        return undefined;
    };

    /**
     * Returns the access_token, if the user is logged in (It will try to relogin with refreshToken if it is possible)
     * @returns {Promise<string>} - access_token or undefined
     */
    const getAccessTokenAsync = async () => {
        const tokens = await getTokensAsync();
        if (tokens) return tokens.accessToken;
        return undefined;
    };

    /**
     * Returns Bearer authorization header. This must be added in the Authorization header of each Api request
     * @returns {Promise<string>} - access_token or undefined
     */
    const getAuthorizationHeaderAsync = async () => {
        const accessToken = await getAccessTokenAsync();
        if (accessToken) return `Bearer ${await accessToken}`;
        return undefined;
    };

    /**
     * Returns true if the user is currently authenticated
     * @returns {Promise<boolean>}
     */
    const isAuthenticatedAsync = async () => !!(await getUserDataAsync());

    /**
     * Check session state and try to use refreshTokens to relogin
     */
    const checkSessionAsync = async () => {
        if (!(await isAuthenticatedAsync())) {
            await getTokensAsync();
        }
    };

    // TODO: generar type para tokens object
    /**
     * Login method
     * @param {import('./types/auth').LoginParamsInterface} params
     * @returns {Promise<Boolean>}
     */
    const logInAsync = async ({
        mode,
        username,
        password,
        connection: overrideConnection,
    }) => {
        if (mode !== 'api') throw new Error('Mode not supported');

        const options = {
            grant_type: 'http://auth0.com/oauth/grant-type/password-realm',
            username,
            password,
            audience,
            realm: overrideConnection || connection,
            client_id: clientId,
            scope: `openid ${useRefreshTokens ? ' offline_access' : ''}`,
            response_type: 'token id_token',
        };

        return oauthRequestAsync(options);
    };

    /**
     * Logout method - Deletes the tokens from the storage
     * @returns {Promise}
     */
    const logOutAsync = () =>
        new Promise((resolve, reject) => {
            try {
                tokenStore.remove();
                resolve();
            } catch (error) {
                reject(error);
            }
        });

    /** Change password - Sends an email with the change password process
     * @param {import('./types/auth').ChangePasswordParamsInterface?} params
     * @returns {Promise<string>}
     */
    const changePasswordAsync = async ({
        email,
        connection: connectionParam,
    } = {}) => {
        try {
            if ((await isAuthenticatedAsync()) || email) {
                const finalEmail = email || (await getUserDataAsync()).email;
                const finalConnection = connectionParam || connection;

                const resp = await fetch(
                    `https://${domain}/dbconnections/change_password`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            client_id: clientId,
                            email: finalEmail,
                            connection: finalConnection,
                        }),
                    }
                );

                if (resp.ok) {
                    return resp.text();
                }
                const data = await resp.json();
                const error = new AuthError('Change password error');
                error.description = data.error;
                throw error;
            }

            const error = new AuthError('Missing param');
            error.description =
                "The user should be logged in, or provide the 'email' param";
            throw error;
        } catch (err) {
            if (!err.description) {
                const error = new UnhandledAuthError('Unhandled error');
                error.original = err;
                error.description =
                    'Unhandled error. Maybe a Network Error. Check console.';
                throw error;
            }
            throw err;
        }
    };

    return {
        logInAsync,
        logOutAsync,
        getAccessTokenAsync,
        getAuthorizationHeaderAsync,
        isAuthenticatedAsync,
        getUserDataAsync,
        checkSessionAsync,
        changePasswordAsync,
    };
};

export default build;
